import type { UpsertDomainPayload } from 'v2/pages/settings/types'
import { createService } from 'v2/shared/services/create-service'
import { BackendServiceName } from 'v2/types'
import type { ChangePlanPayload, HodorService, SsoRequirePayload } from 'v2/types/api/auth'

const HODOR_ENDPOINTS = {
  featureFlagsEvaluate: '/feature-flags/evaluate',
  authorize: '/authorize?show_email_selector=true',
  getDomains: (orgUUID: string) => `/hodor/domains/${orgUUID}`,
  upsertDomains: (orgUUID: string) => `/hodor/domains/${orgUUID}`,
  verifyDomain: (token: string) => `/hodor/domain/verify?token=${token}`,
  ssoConfig: '/accounts/sso-config',
  setupSso: '/accounts/sso-config/setup',
  setRequireSso: '/accounts/sso-config/require-sso',
  outboundAccessStatus: '/contacto-core/outbound-access/status',
  submitOutboundCallingIntake: '/contacto-core/outbound-access/intake',
  changePlan: '/billing/cx/change-plan',
  deactivateOrg: '/hodor/deactivate-org',
  mintBuddyIdentityToken: '/buddy/identity-token',
  subaccountUsersStatus: '/accounts/sub-account-users/status',
  subaccountUsers: '/accounts/sub-account-users',
  subaccountUser: (id: string) => `/accounts/sub-account-users/${encodeURIComponent(id)}`,
  // The embed interceptor rewrites it to /v1/embed/sip-credentials.
  embedSipCredentials: '/sip-credentials',
}

const defaultService = createService('', false, BackendServiceName.default, 'v1')
const regionalHodorService = createService('', true, BackendServiceName.default, 'v1')

/**
 * Creates an instance of the AuthService.
 * @returns An object with methods for authentication.
 */
const serviceFactory = (): HodorService => {
  const service = createService('', false, BackendServiceName.hodor, 'v1')
  return {
    getFeatureFlags: (data) => service.post(HODOR_ENDPOINTS.featureFlagsEvaluate, data),
    authorize: (data) =>
      defaultService.get(HODOR_ENDPOINTS.authorize, data, {
        headers: { 'Client-Type': 'admin_app' },
      }),
    getDomains: (orgUUID: string) => defaultService.get(HODOR_ENDPOINTS.getDomains(orgUUID)),
    upsertDomains: (orgUUID: string, data: UpsertDomainPayload) =>
      defaultService.post(HODOR_ENDPOINTS.upsertDomains(orgUUID), data),
    verifyDomain: (token: string) => defaultService.patch(HODOR_ENDPOINTS.verifyDomain(token), {}),
    getSsoConfig: () => defaultService.get(HODOR_ENDPOINTS.ssoConfig),
    // No body: hodor resolves the email domain from the caller org's Organization record.
    setupSso: () => defaultService.post(HODOR_ENDPOINTS.setupSso, {}),
    setRequireSso: (data: SsoRequirePayload) =>
      defaultService.post(HODOR_ENDPOINTS.setRequireSso, data),
    deleteSso: () => defaultService.delete(HODOR_ENDPOINTS.ssoConfig),
    submitOutboundCallingIntake: (data) =>
      regionalHodorService.post(HODOR_ENDPOINTS.submitOutboundCallingIntake, data),
    getOutboundAccessStatus: () => regionalHodorService.get(HODOR_ENDPOINTS.outboundAccessStatus),
    changePlan: (data: ChangePlanPayload) =>
      regionalHodorService.post(HODOR_ENDPOINTS.changePlan, data),
    deactivateOrg: (data?: Record<string, unknown>) =>
      regionalHodorService.post(HODOR_ENDPOINTS.deactivateOrg, data || {}),
    mintBuddyIdentityToken: () =>
      regionalHodorService.post(HODOR_ENDPOINTS.mintBuddyIdentityToken, {}),
    getSubaccountUsersStatus: () => defaultService.get(HODOR_ENDPOINTS.subaccountUsersStatus),
    addSubaccountUser: (data) => defaultService.post(HODOR_ENDPOINTS.subaccountUsers, data),
    removeSubaccountUser: (id) => defaultService.delete(HODOR_ENDPOINTS.subaccountUser(id)),
    getEmbedSipCredentials: () => regionalHodorService.get(HODOR_ENDPOINTS.embedSipCredentials),
  }
}

/**
 * A service instance for the authentication API or the Hodor Service (Global)
 * @returns Available methods for authentication.
 */
export const hodorService = serviceFactory()
